import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2, AlertTriangle, ChevronDown, SlidersHorizontal } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { MultiSelect } from "../ui/multi-select";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import { cn } from "../../lib/utils";

const CampaignSchema = Yup.object().shape({
	name: Yup.string().min(2, "Muito curto!").required("Obrigatório"),
	body: Yup.string().required("Obrigatório"),
	whatsappId: Yup.string().required("Obrigatório"),
	intervalSeconds: Yup.number().min(1, "Mínimo 1").required("Obrigatório"),
});

const CampaignModal = ({ open, onClose, campaignId }) => {
	const initialState = {
		name: "",
		body: "",
		whatsappId: "",
		mediaUrl: "",
		intervalSeconds: 20,
		scheduledFor: "",
		phraseListId: "",
	};

	const [listasDeFrases, setListasDeFrases] = useState([]);

	const [campaign, setCampaign] = useState(initialState);
	const { whatsApps } = useWhatsApps();

	const [targetTab, setTargetTab] = useState("contacts");
	const [contactOptions, setContactOptions] = useState([]);
	const [selectedContacts, setSelectedContacts] = useState([]);
	const [contactSearch, setContactSearch] = useState("");
	const [tags, setTags] = useState([]);
	const [queues, setQueues] = useState([]);
	// Respostas prontas para reaproveitar aqui: escrever a mesma promoção do
	// zero a cada campanha é o que elas existem para evitar.
	const [modelos, setModelos] = useState([]);
	const [listas, setListas] = useState([]);
	const [selectedListId, setSelectedListId] = useState("");
	const [selectedTagId, setSelectedTagId] = useState("");
	const [selectedQueueId, setSelectedQueueId] = useState("");
	// Ritmo, mídia e a anotação de data quase nunca mudam. Ficam guardados
	// atrás de um resumo para o formulário caber numa olhada.
	const [avancadoAberto, setAvancadoAberto] = useState(false);

	useEffect(() => {
		if (!open) return;
		(async () => {
			try {
				const { data } = await api.get("/tags");
				setTags(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
			try {
				const { data } = await api.get("/campaign-phrases");
				setListasDeFrases(Array.isArray(data) ? data : []);
			} catch (err) {
				// Sem listas de frases a campanha manda o texto fixo, como sempre.
			}
			try {
				const { data } = await api.get("/queue");
				setQueues(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
			try {
				const { data } = await api.get("/contact-lists");
				setListas(data?.lists || (Array.isArray(data) ? data : []));
			} catch (err) {
				// Idem: sem listas, a aba fica vazia e as outras continuam.
			}
			try {
				// Lê das respostas rápidas: era uma tela separada de "modelos de
				// mensagem" que guardava a mesma coisa, e quem escrevia um texto
				// novo tinha de adivinhar em qual das duas salvar.
				const { data } = await api.get("/quickAnswers", {
					params: { pageNumber: 1 },
				});
				setModelos(data?.quickAnswers || []);
			} catch (err) {
				// Sem respostas salvas o campo some; escrever a mensagem na mão
				// continua funcionando.
			}
		})();
	}, [open]);

	useEffect(() => {
		if (!open) return;
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("/contacts/", {
						params: { searchParam: contactSearch },
					});
					setContactOptions(data.contacts || []);
				} catch (err) {
					toastError(err);
				}
			};
			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [contactSearch, open]);

	useEffect(() => {
		(async () => {
			if (!campaignId) return;
			try {
				const { data } = await api.get(`/campaigns/${campaignId}`);
				setCampaign((prevState) => ({
					...prevState,
					name: data.name || "",
					body: data.body || "",
					whatsappId: data.whatsappId ? String(data.whatsappId) : "",
					mediaUrl: data.mediaUrl || "",
					intervalSeconds: data.intervalSeconds || 20,
					scheduledFor: data.scheduledFor
						? new Date(data.scheduledFor).toISOString().slice(0, 16)
						: "",
					phraseListId: data.phraseListId ? String(data.phraseListId) : "",
				}));
			} catch (err) {
				toastError(err);
			}
		})();

		return () => {
			setCampaign(initialState);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campaignId, open]);

	const handleClose = () => {
		onClose();
		setCampaign(initialState);
		setSelectedContacts([]);
		setContactSearch("");
		setSelectedListId("");
		setSelectedTagId("");
		setSelectedQueueId("");
		setTargetTab("contacts");
		setAvancadoAberto(false);
	};

	// Quantas pessoas essa campanha vai atingir, em português.
	const listaEscolhida = listas.find(
		(l) => String(l.id) === String(selectedListId)
	);
	const resumoDestinatarios = (() => {
		if (targetTab === "contacts") {
			return selectedContacts.length
				? `${selectedContacts.length} contato(s) escolhido(s) na mão`
				: "nenhum contato escolhido ainda";
		}
		if (targetTab === "list") {
			if (!listaEscolhida) return "nenhuma lista escolhida ainda";
			const qtd = listaEscolhida.items?.length;
			return qtd
				? `todos os ${qtd} contatos da lista “${listaEscolhida.name}”`
				: `todos os contatos da lista “${listaEscolhida.name}”`;
		}
		if (targetTab === "tag") {
			const tag = tags.find((t) => String(t.id) === String(selectedTagId));
			return tag
				? `todos os contatos marcados com a tag “${tag.name}”`
				: "nenhuma tag escolhida ainda";
		}
		const fila = queues.find((q) => String(q.id) === String(selectedQueueId));
		return fila
			? `todos os contatos que já foram atendidos na fila “${fila.name}”`
			: "nenhuma fila escolhida ainda";
	})();

	const mergedContactOptions = (() => {
		const map = new Map();
		selectedContacts.forEach((c) => map.set(String(c.id), c));
		contactOptions.forEach((c) => map.set(String(c.id), c));
		return Array.from(map.values()).map((c) => ({
			value: String(c.id),
			label: c.name,
		}));
	})();

	const handleContactsChange = (values) => {
		const lookup = new Map();
		selectedContacts.forEach((c) => lookup.set(String(c.id), c));
		contactOptions.forEach((c) => lookup.set(String(c.id), c));
		setSelectedContacts(
			values.map((v) => lookup.get(v)).filter((c) => Boolean(c))
		);
	};

	const handleSaveCampaign = async (values) => {
		const payload = {
			name: values.name,
			body: values.body,
			whatsappId: +values.whatsappId,
			mediaUrl: values.mediaUrl || null,
			intervalSeconds: +values.intervalSeconds || 20,
			scheduledFor: values.scheduledFor || null,
			phraseListId: values.phraseListId ? +values.phraseListId : null,
		};

		try {
			if (campaignId) {
				await api.put(`/campaigns/${campaignId}`, payload);
			} else {
				if (targetTab === "contacts" && selectedContacts.length > 0) {
					payload.contactIds = selectedContacts.map((c) => +c.id);
				}
				if (targetTab === "list" && selectedListId) {
					payload.contactListId = +selectedListId;
				}
				if (targetTab === "tag" && selectedTagId) {
					payload.tagId = +selectedTagId;
				}
				if (targetTab === "queue" && selectedQueueId) {
					payload.queueId = +selectedQueueId;
				}
				await api.post("/campaigns", payload);
			}
			toast.success("Campanha salva com sucesso.");
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{campaignId
							? "Editar campanha"
							: "Preparar um envio para vários contatos"}
					</DialogTitle>
					<p className="text-xs leading-relaxed text-muted-foreground">
						Nada é enviado ao salvar. A campanha fica parada até alguém clicar
						em Iniciar na lista.
					</p>
				</DialogHeader>
				<Formik
					initialValues={campaign}
					enableReinitialize={true}
					validationSchema={CampaignSchema}
					onSubmit={(values, actions) => {
						setTimeout(async () => {
							await handleSaveCampaign(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form className="space-y-4">
							<div className="space-y-1.5">
								<Label htmlFor="name">Nome da campanha</Label>
								<Field
									as={Input}
									id="name"
									name="name"
									autoFocus
									placeholder="Promoção de julho"
								/>
								<p className="text-xs text-muted-foreground">
									Só para você achar depois. O cliente não vê esse nome.
								</p>
								{touched.name && errors.name && (
									<p className="text-xs text-destructive">{errors.name}</p>
								)}
							</div>

							<div className="space-y-1.5">
								<Label>Sai por qual número</Label>
								<Select
									value={values.whatsappId ? String(values.whatsappId) : undefined}
									onValueChange={(v) => setFieldValue("whatsappId", v)}
								>
									<SelectTrigger>
										<SelectValue placeholder="Selecione a conexão" />
									</SelectTrigger>
									<SelectContent>
										{whatsApps.map((whatsapp) => (
											<SelectItem key={whatsapp.id} value={String(whatsapp.id)}>
												{whatsapp.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									É desse WhatsApp que os clientes vão receber — e é nele que eles
									vão responder.
								</p>
								{touched.whatsappId && errors.whatsappId && (
									<p className="text-xs text-destructive">{errors.whatsappId}</p>
								)}
							</div>

							<div className="space-y-1.5">
								<div className="flex items-center justify-between gap-2">
									<Label htmlFor="body">O que cada pessoa vai receber</Label>
									{modelos.length > 0 && (
										<Select
											value=""
											onValueChange={(v) => {
												const modelo = modelos.find((m) => String(m.id) === v);
												// Preenche o campo em vez de guardar o vínculo: quem
												// monta a campanha quase sempre ajusta o texto, e um
												// modelo editado depois não deve mudar o que já saiu.
												if (modelo) setFieldValue("body", modelo.message || "");
											}}
										>
											<SelectTrigger className="h-8 w-52 text-xs">
												<SelectValue placeholder="Usar uma resposta pronta" />
											</SelectTrigger>
											<SelectContent>
												{modelos.map((modelo) => (
													<SelectItem key={modelo.id} value={String(modelo.id)}>
														{modelo.shortcut}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								</div>
								<Field
									as={Textarea}
									id="body"
									name="body"
									rows={4}
									placeholder="Olá {{name}}, tudo bem?"
								/>
								<p className="text-xs text-muted-foreground">
									{
										"Todo mundo recebe o mesmo texto. Escreva {{name}} onde quiser o nome da pessoa — “Olá {{name}}” chega como “Olá Maria”."
									}
								</p>
								{touched.body && errors.body && (
									<p className="text-xs text-destructive">{errors.body}</p>
								)}
							</div>

							{/* Guardado atrás de um resumo: quem não precisa mexer,
							    entende o estado sem abrir. */}
							<div className="rounded-xl border">
								<button
									type="button"
									onClick={() => setAvancadoAberto((v) => !v)}
									className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted/50"
								>
									<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
										<SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
									</span>
									<span className="min-w-0 flex-1">
										<span className="block text-sm font-medium">
											Ritmo de envio e anexo
										</span>
										<span className="block text-xs leading-relaxed text-muted-foreground">
											Uma mensagem a cada {values.intervalSeconds || 20}{" "}
											segundo(s)
											{values.mediaUrl ? " · com um link de anexo" : " · sem anexo"}
											{values.scheduledFor ? " · com data anotada" : ""}
										</span>
									</span>
									<ChevronDown
										className={cn(
											"h-4 w-4 shrink-0 text-muted-foreground transition-transform",
											avancadoAberto && "rotate-180"
										)}
									/>
								</button>

								{avancadoAberto && (
									<div className="space-y-4 border-t p-3">
										<div className="space-y-1.5">
											<Label htmlFor="intervalSeconds">
												Esperar quantos segundos entre uma mensagem e outra
											</Label>
											<Field
												as={Input}
												id="intervalSeconds"
												name="intervalSeconds"
												type="number"
												min={1}
											/>
											<p className="text-xs text-muted-foreground">
												Com 20 segundos, 100 contatos levam mais ou menos 33
												minutos. Deixe assim se estiver na dúvida.
											</p>
											{Number(values.intervalSeconds) > 0 &&
												Number(values.intervalSeconds) < 10 && (
													<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
														<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
														Menos de 10 segundos dispara muita mensagem em pouco
														tempo. O WhatsApp pode entender como spam e bloquear
														o seu número.
													</p>
												)}
											{touched.intervalSeconds && errors.intervalSeconds && (
												<p className="text-xs text-destructive">
													{errors.intervalSeconds}
												</p>
											)}
										</div>

										{listasDeFrases.length > 0 && (
											<div className="space-y-1.5">
												<Label htmlFor="phraseListId">
													Variar o texto por uma lista de frases
												</Label>
												<Select
													value={values.phraseListId || "nenhuma"}
													onValueChange={(v) =>
														setFieldValue(
															"phraseListId",
															v === "nenhuma" ? "" : v
														)
													}
												>
													<SelectTrigger id="phraseListId">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="nenhuma">
															Não variar — usar sempre o texto acima
														</SelectItem>
														{listasDeFrases.map((lista) => (
															<SelectItem
																key={lista.id}
																value={String(lista.id)}
															>
																{lista.name} ({lista.quantidade} variações)
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												<p className="text-xs leading-relaxed text-muted-foreground">
													Cada contato recebe uma frase sorteada da lista, em
													vez do texto acima. Mil mensagens idênticas no mesmo
													minuto é o que o WhatsApp usa para identificar
													disparo em massa.
												</p>
											</div>
										)}

										<div className="space-y-1.5">
											<Label htmlFor="mediaUrl">
												Link de uma imagem ou arquivo (opcional)
											</Label>
											<Field
												as={Input}
												id="mediaUrl"
												name="mediaUrl"
												placeholder="https://..."
											/>
											<p className="text-xs text-muted-foreground">
												O link é colado no final da mensagem. Em branco, vai só o
												texto.
											</p>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="scheduledFor">
												Data que você pretende enviar (opcional)
											</Label>
											<Field
												as={Input}
												id="scheduledFor"
												name="scheduledFor"
												type="datetime-local"
											/>
											<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
												<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
												Isto é só uma anotação para você se organizar — o sistema
												não dispara sozinho nessa hora. O envio começa quando
												alguém clicar em Iniciar.
											</p>
										</div>
									</div>
								)}
							</div>

							{!campaignId && (
								<div className="space-y-1.5">
									<Label>Quem vai receber</Label>
									<p className="text-xs text-muted-foreground">
										Escolha de um jeito só. Depois de criada, a lista de quem
										recebe não muda mais.
									</p>
									<Tabs value={targetTab} onValueChange={setTargetTab}>
										<TabsList className="grid w-full grid-cols-4">
											<TabsTrigger value="contacts">Escolher</TabsTrigger>
											<TabsTrigger value="list">Uma lista</TabsTrigger>
											<TabsTrigger value="tag">Por tag</TabsTrigger>
											<TabsTrigger value="queue">Por fila</TabsTrigger>
										</TabsList>
										<TabsContent value="list" className="pt-2">
											<Select
												value={selectedListId || undefined}
												onValueChange={setSelectedListId}
											>
												<SelectTrigger>
													<SelectValue placeholder="Selecione uma lista" />
												</SelectTrigger>
												<SelectContent>
													{listas.map((lista) => (
														<SelectItem key={lista.id} value={String(lista.id)}>
															{lista.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<p className="pt-1.5 text-xs text-muted-foreground">
												Quem ainda não é contato do sistema é cadastrado ao criar
												a campanha.
											</p>
										</TabsContent>
										<TabsContent value="contacts" className="space-y-2 pt-2">
											<Input
												placeholder="Buscar contatos..."
												value={contactSearch}
												onChange={(e) => setContactSearch(e.target.value)}
											/>
											<MultiSelect
												options={mergedContactOptions}
												value={selectedContacts.map((c) => String(c.id))}
												onChange={handleContactsChange}
												placeholder="Selecione os contatos"
											/>
										</TabsContent>
										<TabsContent value="tag" className="pt-2">
											<p className="pb-1.5 text-xs text-muted-foreground">
												Todo contato que tiver essa tag hoje entra na campanha.
											</p>
											<Select
												value={selectedTagId || undefined}
												onValueChange={setSelectedTagId}
											>
												<SelectTrigger>
													<SelectValue placeholder="Selecione uma tag" />
												</SelectTrigger>
												<SelectContent>
													{tags.map((tag) => (
														<SelectItem key={tag.id} value={String(tag.id)}>
															{tag.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</TabsContent>
										<TabsContent value="queue" className="pt-2">
											<p className="pb-1.5 text-xs text-muted-foreground">
												Todo mundo que já foi atendido nessa fila entra na
												campanha — pode ser bastante gente.
											</p>
											<Select
												value={selectedQueueId || undefined}
												onValueChange={setSelectedQueueId}
											>
												<SelectTrigger>
													<SelectValue placeholder="Selecione uma fila" />
												</SelectTrigger>
												<SelectContent>
													{queues.map((queue) => (
														<SelectItem key={queue.id} value={String(queue.id)}>
															{queue.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</TabsContent>
									</Tabs>
								</div>
							)}

							{/* A frase final, antes do botão: o que vai acontecer. */}
							<div className="rounded-md bg-muted p-2.5 text-xs leading-relaxed">
								<p className="flex gap-1.5">
									<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
									<span>
										{campaignId ? (
											<>
												Salvar só muda o texto e os ajustes. Quem recebe continua
												sendo a mesma lista, e nada é enviado agora.
											</>
										) : (
											<>
												Vai atingir{" "}
												<span className="font-medium">{resumoDestinatarios}</span>
												, uma mensagem a cada {values.intervalSeconds || 20}{" "}
												segundo(s)
												{whatsApps.find(
													(w) => String(w.id) === String(values.whatsappId)
												)
													? `, pelo WhatsApp ${
															whatsApps.find(
																(w) => String(w.id) === String(values.whatsappId)
															).name
													  }`
													: ""}
												.
											</>
										)}
									</span>
								</p>
								{!campaignId && (
									<p className="mt-1.5 text-muted-foreground">
										Ao criar, ninguém recebe nada ainda. O envio começa no botão
										de play da lista, e o sistema pede confirmação antes.
									</p>
								)}
							</div>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									Cancelar
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									{campaignId ? "Salvar alterações" : "Criar sem enviar ainda"}
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default CampaignModal;
